/**
 * NEON OVERPASS — pseudo-3D OutRun-style racer.
 *
 * Faithful port of `year4.vercel.app/racecar.html`'s mechanics (segment-based
 * road, perspective projection, opponent traffic, off-road decel) onto the
 * Arcadia '84 cartridge contract. The big architectural adaptation:
 *
 * - All randomness routes through `ctx.random` (xoshiro256** seeded PRNG)
 *   so a given (seed, input-frame-sequence) always produces the same final
 *   score. Verified by the 1000-iteration determinism test in
 *   `instance.test.ts`.
 * - We run fixed-timestep 60Hz (the cabinet's contract). The reference is
 *   variable-timestep. We trade visual smoothness on 120Hz displays for
 *   replay-validatable runs.
 *
 * @see road.ts for the projection math and track construction.
 * @see palette.ts for the color tokens.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
  LoopHandle,
} from '@arcraz/arcadia-types';
import {
  CRASH_FLASH,
  FOG_COLOR,
  HUD_SCORE,
  HUD_SPEED,
  HUD_TEXT,
  HUD_TURBO,
  MOUNTAIN_FAR,
  MOUNTAIN_NEAR,
  PLAYER_BODY,
  PLAYER_BODY_DARK,
  PLAYER_BODY_LIGHT,
  PLAYER_TAIL,
  PLAYER_WINDOW,
  PLAYER_WINDOW_HI,
  SKY_HORIZON,
  SKY_TOP,
  SPRITE_NEON_CYAN,
  SPRITE_NEON_PINK,
  SPRITE_PALM_FRONDS,
  SPRITE_PALM_HI,
  SPRITE_PALM_TRUNK,
  SPRITE_SIGN_BORDER,
  SPRITE_SIGN_POST,
  SPRITE_SIGN_YELLOW,
  SUN,
  TRAFFIC_CYAN,
  TRAFFIC_GREEN,
  TRAFFIC_MAGENTA,
  TRAFFIC_PURPLE,
  TRAFFIC_SHADOW,
} from './palette.js';
import {
  ACCEL,
  BRAKING,
  buildTrack,
  CAMERA_DEPTH,
  CAMERA_HEIGHT,
  CENTRIFUGAL,
  clamp,
  DECEL,
  DRAW_DISTANCE,
  expFog,
  findSegment,
  FOG_DENSITY,
  increase,
  lerp,
  MAX_SPEED,
  OFF_ROAD_DECEL,
  OFF_ROAD_LIMIT,
  type OpponentCar,
  placeCars,
  placeSprites,
  PLAYER_Z,
  project,
  type RoadsideSprite,
  ROAD_WIDTH,
  type Segment,
  SEGMENT_LENGTH,
  TOTAL_LAPS,
  TURBO_MULT,
} from './road.js';

const WIDTH = 480;
const HEIGHT = 270;

const PLAYER_SCREEN_Y = HEIGHT - 32;
// Player sprite is drawn at fixed screen size (no perspective scaling)
// because it sits at camera Z. Calibrated against opponent billboard math
// at typical near-distance: opponents render ~78px wide, so player at 90×46
// reads as "closer than opponents" — which is correct geometry.
const PLAYER_SPRITE_W = 90;
const PLAYER_SPRITE_H = 46;

/** Player sprite world-width — used for collision against opponent cars. */
const PLAYER_WORLD_W = 650 * 0.6;

const CRASH_COOLDOWN_TICKS = 36; // 0.6s @ 60fps
const CRASH_FLASH_TICKS = 18;
const SHAKE_TICKS = 14; // ~0.23s
const SHAKE_MAGNITUDE = 3; // peak pixel offset at full intensity

type Phase = 'driving' | 'crashed' | 'finished' | 'game-over';

export class NeonOverpassInstance implements GameInstance {
  private readonly ctx: GameContext;

  // Track + cars (built once per run, mutated each tick).
  private segments: Segment[] = [];
  private trackLength = 0;
  private cars: OpponentCar[] = [];

  // Player state.
  private position = 0; // world Z
  private speed = 0; // world units / second
  private playerX = 0; // normalized -1..1 (rumble strip = +/-1)
  private lap = 1;
  private elapsed = 0; // seconds since start
  private score = 0;
  private finalScore = 0;
  private finalElapsed = 0;
  private crashCooldown = 0; // ticks
  private crashFlashLeft = 0;
  // Screen-shake on collision (sc-2830). Decrements each tick; render()
  // applies a small PRNG-derived translation to the canvas while > 0.
  private shakeTicksLeft = 0;
  // Engine drone (sc-2858 audio). Two detuned sawtooth voices + FM modulator
  // = race-car growl. Started on first update tick, frequency modulated
  // each tick from this.speed, stopped on teardown.
  private engineCarrier: LoopHandle | null = null;
  private engineDetune: LoopHandle | null = null;

  // Inputs (latched each tick).
  private inLeft = false;
  private inRight = false;
  private inAccel = false;
  private inBrake = false;
  private inTurbo = false;

  // Lifecycle.
  private phase: Phase = 'driving';
  private restartArmed = false;

  // Background parallax (cosmetic).
  private bgCurve = 0;
  private bgHill = 0;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.buildRun();
  }

  // --------------------------------------------------------------------------
  // GameInstance contract
  // --------------------------------------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over' || this.phase === 'finished') {
      if (state.justPressed.has('fireA')) {
        this.restartArmed = true;
      }
      return;
    }
    this.inLeft = state.left;
    this.inRight = state.right;
    this.inAccel = state.up || state.fireA;
    this.inBrake = state.down;
    this.inTurbo = state.fireB;
  }

  update(_dt: number): void {
    // Fixed-step contract: cabinet drives us at 60Hz. `_dt` is unused; we
    // treat one tick as 1/60s for the physics math below.
    const dt = 1 / 60;

    if (this.phase === 'crashed') {
      // Brief flash window before resuming control.
      this.crashFlashLeft -= 1;
      if (this.shakeTicksLeft > 0) this.shakeTicksLeft -= 1;
      this.bgCurve = 0;
      this.bgHill = 0;
      // Cut engine gain while crashed so the high-RPM pitch doesn't
      // sit on the player's ears through the flash.
      this.engineCarrier?.setGain(0);
      this.engineDetune?.setGain(0);
      if (this.crashFlashLeft <= 0) {
        this.phase = 'driving';
      }
      return;
    }
    if (this.shakeTicksLeft > 0) this.shakeTicksLeft -= 1;

    if (this.phase !== 'driving') {
      return;
    }

    this.elapsed += dt;

    // Engine drone: lazy-start two detuned sawtooth voices + FM growl.
    // Frequency = idle 70Hz at zero speed, ramping to 280Hz at MAX_SPEED.
    // Detune voice runs 7 cents flat for chorus thickness.
    const speedFrac = Math.max(0, this.speed / MAX_SPEED);
    const engineHz = 70 + 210 * speedFrac;
    if (!this.engineCarrier) {
      this.engineCarrier = this.ctx.audio.loop({
        wave: 'sawtooth',
        f: engineHz,
        gain: 0.18,
        fm: { ratio: 2.0, index: 14 },
      });
      this.engineDetune = this.ctx.audio.loop({
        wave: 'sawtooth',
        f: engineHz,
        gain: 0.1,
        detune: -7,
      });
    }
    this.engineCarrier.setFrequency(engineHz);
    this.engineDetune?.setFrequency(engineHz);
    // Slight gain swell with speed so it FEELS like the engine working.
    const engineGain = 0.12 + 0.14 * speedFrac;
    this.engineCarrier.setGain(engineGain);
    this.engineDetune?.setGain(engineGain * 0.55);

    const playerSegPre = findSegment(this.segments, this.position + PLAYER_Z);
    const speedPercent = this.speed / MAX_SPEED;
    const dx = dt * 2.2 * speedPercent;

    // Advance opponents and re-bucket into their segments.
    for (const car of this.cars) {
      const oldSeg = findSegment(this.segments, car.z);
      car.z = increase(car.z, dt * car.speed, this.trackLength);
      car.percent = (car.z % SEGMENT_LENGTH) / SEGMENT_LENGTH;
      const newSeg = findSegment(this.segments, car.z);
      if (oldSeg !== newSeg) {
        const idx = oldSeg.cars.indexOf(car);
        if (idx >= 0) {
          oldSeg.cars.splice(idx, 1);
        }
        newSeg.cars.push(car);
      }
    }

    // Advance player position; detect lap wrap.
    const startPos = this.position;
    this.position = increase(this.position, dt * this.speed, this.trackLength);
    if (this.position < startPos - 1) {
      this.lap += 1;
      this.score += 5000;
      this.ctx.audio.sfx('lap');
      if (this.lap > TOTAL_LAPS) {
        this.phase = 'finished';
        this.finalScore = Math.floor(this.score);
        this.finalElapsed = this.elapsed;
        this.ctx.events.emit('death', { remainingLives: 0 });
        return;
      }
      // Difficulty ramp (sc-2830): each completed lap speeds up every
      // opponent by 20%. Deterministic — derives from `lap`, no clock /
      // PRNG drift. Lap 2: 1.2×. Lap 3: 1.44×. Stacks multiplicatively
      // so a stranded slow truck still gets dangerous by lap 3.
      const ramp = 1.2;
      for (const car of this.cars) {
        car.speed *= ramp;
      }
    }

    // Steering.
    if (this.inLeft) this.playerX -= dx;
    if (this.inRight) this.playerX += dx;
    // Centrifugal pull on curves.
    this.playerX -= dx * speedPercent * playerSegPre.curve * CENTRIFUGAL;

    // Accel / brake / decel.
    if (this.inAccel) this.speed += ACCEL * dt;
    else if (this.inBrake) this.speed += BRAKING * dt;
    else this.speed += DECEL * dt;

    // Off-road decel.
    const offRoad = this.playerX < -1 || this.playerX > 1;
    if (offRoad && this.speed > OFF_ROAD_LIMIT) {
      this.speed += OFF_ROAD_DECEL * dt;
    }

    // Roadside collision.
    if (offRoad) {
      for (const s of playerSegPre.sprites) {
        const sw = s.worldW / ROAD_WIDTH;
        if (this.overlap(this.playerX, PLAYER_WORLD_W / ROAD_WIDTH, s.offset, sw, 0.6)) {
          this.triggerCrash(playerSegPre.p1.world.z);
          break;
        }
      }
    }

    // Opponent collision (only when overtaking from behind).
    if (this.phase === 'driving') {
      for (const c of playerSegPre.cars) {
        const cw = c.worldW / ROAD_WIDTH;
        if (this.speed > c.speed) {
          if (this.overlap(this.playerX, PLAYER_WORLD_W / ROAD_WIDTH, c.offset, cw, 0.7)) {
            this.speed = c.speed * (c.speed / Math.max(1, this.speed));
            this.position = increase(c.z, -PLAYER_Z, this.trackLength);
            this.triggerCrash(c.z);
            break;
          }
        }
      }
    }

    // Crash cooldown ticks down whether or not we're crashed; cabinet handles
    // the flash phase separately.
    if (this.crashCooldown > 0) this.crashCooldown -= 1;

    this.playerX = clamp(this.playerX, -2.5, 2.5);
    const topSpeed = MAX_SPEED * (this.inTurbo ? TURBO_MULT : 1);
    this.speed = clamp(this.speed, 0, topSpeed);

    // Score: time + speed bonus.
    this.score += dt * (10 + speedPercent * 40);

    // Background parallax.
    this.bgCurve = increase(
      this.bgCurve,
      (dt * this.speed * playerSegPre.curve) / SEGMENT_LENGTH * 0.7,
      WIDTH * 4
    );
    const targetHill = (playerSegPre.p2.world.y - playerSegPre.p1.world.y) * 0.0015;
    this.bgHill += (targetHill * 6 - this.bgHill) * Math.min(1, dt * 4);
  }

  render(g: CanvasRenderingContext2D): void {
    // Screen-shake on collision (sc-2830). Translate the whole frame by a
    // small offset that decays over SHAKE_TICKS. Source of "randomness" is
    // the shake counter itself (sine + cosine), NOT ctx.random — render
    // must never consume PRNG state, or headless replays would diverge
    // because render isn't called in the headless loop. The matching
    // g.restore() lives further down, just before renderHUD (HUD must
    // stay rock-steady, not shake with the scene).
    if (this.shakeTicksLeft > 0) {
      const t = this.shakeTicksLeft;
      const intensity = (t / SHAKE_TICKS) * SHAKE_MAGNITUDE;
      const sx = Math.sin(t * 12.91) * intensity;
      const sy = Math.cos(t * 9.37) * intensity;
      g.save();
      g.translate(Math.round(sx), Math.round(sy));
    }

    const baseSeg = findSegment(this.segments, this.position);
    const basePercent = (this.position % SEGMENT_LENGTH) / SEGMENT_LENGTH;
    const playerSeg = findSegment(this.segments, this.position + PLAYER_Z);
    const playerPercent = ((this.position + PLAYER_Z) % SEGMENT_LENGTH) / SEGMENT_LENGTH;
    const playerY = lerp(playerSeg.p1.world.y, playerSeg.p2.world.y, playerPercent);
    // `maxY` is the highest-on-screen pixel a closer segment has already
    // painted. New segments cull if their FAR edge `p2.screen.y` is at-or-
    // above that line — segment occlusion. Initialize to canvas height so
    // the very first (closest) segment always paints; it then anchors maxY
    // for every farther segment.
    //
    // Wrap-around bug (track loops past horizon and paints phantom bands):
    // handled separately below by skipping segments whose fog factor falls
    // below FOG_HORIZON_CLIP. The `maxY` value is NOT the right place to
    // enforce horizon clipping — that breaks the closest segment.
    let maxY = HEIGHT;

    let dxRender = -(baseSeg.curve * basePercent);
    let xOffset = 0;

    // Sky + horizon backdrop.
    this.renderBackground(g, playerY);

    // Project + render segments from near to far.
    for (let n = 0; n < DRAW_DISTANCE; n++) {
      const seg = this.segments[(baseSeg.index + n) % this.segments.length] as Segment;
      seg.looped = seg.index < baseSeg.index;
      seg.fog = expFog(n / DRAW_DISTANCE, FOG_DENSITY);
      seg.clip = maxY;

      project(
        seg.p1,
        this.playerX * ROAD_WIDTH - xOffset,
        playerY + CAMERA_HEIGHT,
        this.position - (seg.looped ? this.trackLength : 0),
        WIDTH,
        HEIGHT
      );
      project(
        seg.p2,
        this.playerX * ROAD_WIDTH - xOffset - dxRender,
        playerY + CAMERA_HEIGHT,
        this.position - (seg.looped ? this.trackLength : 0),
        WIDTH,
        HEIGHT
      );

      xOffset += dxRender;
      dxRender += seg.curve;

      // Cull: behind camera (camera.z below near-clip), wrong-side-up (back
      // edge LOWER than near edge — happens with wrapped or hill-crested
      // segments), occluded by a closer-already-painted segment (back edge
      // >= maxY), beyond a hard far-clip (kills wrap-around "phantom road"
      // bands from segments at the back of the array projecting to the
      // horizon line via the `seg.looped` Z-subtraction), or visually
      // degenerate (segment body less than 1 pixel tall — its rumble/lane
      // polygons collapse to single-pixel horizontal lines at the horizon).
      //
      // FAR_CLIP_Z = SEGMENT_LENGTH * 100 = 20000 world units. That's well
      // past the natural fog horizon but short of where wrapped segments
      // start projecting. Tunable; lower = sharper horizon but less depth.
      const FAR_CLIP_Z = SEGMENT_LENGTH * 100;
      if (
        seg.p1.camera.z <= CAMERA_DEPTH ||
        seg.p1.camera.z > FAR_CLIP_Z ||
        seg.p2.screen.y >= seg.p1.screen.y ||
        seg.p2.screen.y >= maxY ||
        seg.p1.screen.y - seg.p2.screen.y < 1
      ) {
        continue;
      }

      this.renderSegment(g, seg);
      maxY = seg.p2.screen.y;
    }

    // Sprites + opponent cars: render far to near so closer ones overdraw.
    // Same far-clip as the segment pass — without it, wrapped-track cars
    // and sprites still paint at the wrap-around horizon even though the
    // road they sit on was already culled.
    const FAR_CLIP_Z_SPRITES = SEGMENT_LENGTH * 100;
    for (let n = DRAW_DISTANCE - 1; n > 0; n--) {
      const seg = this.segments[(baseSeg.index + n) % this.segments.length] as Segment;
      if (seg.p1.camera.z <= CAMERA_DEPTH || seg.p1.camera.z > FAR_CLIP_Z_SPRITES) {
        continue;
      }
      for (const car of seg.cars) {
        const sScale = lerp(seg.p1.screen.scale, seg.p2.screen.scale, car.percent);
        const sX =
          lerp(seg.p1.screen.x, seg.p2.screen.x, car.percent) +
          (sScale * car.offset * ROAD_WIDTH * WIDTH) / 2;
        const sY = lerp(seg.p1.screen.y, seg.p2.screen.y, car.percent);
        this.renderCarSprite(g, car, sScale, sX, sY, seg.clip);
      }
      for (const sp of seg.sprites) {
        const sScale = seg.p1.screen.scale;
        const sX = seg.p1.screen.x + (sScale * sp.offset * ROAD_WIDTH * WIDTH) / 2;
        const sY = seg.p1.screen.y;
        this.renderRoadsideSprite(g, sp, sScale, sX, sY, seg.clip);
      }
    }

    // Player car.
    const visualSteer = (this.inRight ? 1 : 0) - (this.inLeft ? 1 : 0);
    this.renderPlayer(g, visualSteer, this.inBrake);

    // Turbo speed-streaks: removed in sc-2830. The previous implementation
    // painted 10 white 10×1 dashes at position-derived offsets that didn't
    // flow with the road; they read as artifacts, not motion. A proper
    // outward-flowing streak effect anchored to the road geometry is a
    // followup (filed under NEON OVERPASS gameplay polish).

    // Crash flash overlay — drawn inside the shake transform so the red
    // tint shakes with the scene.
    if (this.phase === 'crashed' && this.crashFlashLeft > 0) {
      const a = this.crashFlashLeft / CRASH_FLASH_TICKS;
      g.globalAlpha = a * 0.7;
      g.fillStyle = CRASH_FLASH;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    // Pop the shake transform BEFORE HUD — the HUD must stay rock-steady.
    if (this.shakeTicksLeft > 0) {
      g.restore();
    }

    this.renderHUD(g);

    if (this.phase === 'finished' || this.phase === 'game-over') {
      this.renderGameOver(g);
    }
  }

  resize(_w: number, _h: number): void {
    // 480×270 internal resolution is the canonical cabinet contract.
  }

  pause(): void {
    // No audio loops yet.
  }

  resume(): void {
    // No audio loops yet.
  }

  teardown(): void {
    this.segments = [];
    this.cars = [];
    this.engineCarrier?.stop();
    this.engineDetune?.stop();
    this.engineCarrier = null;
    this.engineDetune = null;
  }

  getScore(): number {
    return Math.floor(this.score);
  }

  getFinalScore(): number {
    if (this.phase === 'finished' || this.phase === 'game-over') {
      return this.finalScore;
    }
    return Math.floor(this.score);
  }

  isGameOver(): boolean {
    return this.phase === 'finished' || this.phase === 'game-over';
  }

  /** Cabinet convention: cabinet polls this to know when to spin up a new run. */
  shouldRestart(): boolean {
    return (
      (this.phase === 'finished' || this.phase === 'game-over') && this.restartArmed
    );
  }

  // --------------------------------------------------------------------------
  // Internal helpers
  // --------------------------------------------------------------------------

  private buildRun(): void {
    this.segments = buildTrack();
    placeSprites(this.segments, this.ctx.random);
    this.cars = placeCars(this.segments, this.ctx.random, MAX_SPEED);
    this.trackLength = this.segments.length * SEGMENT_LENGTH;
  }

  private triggerCrash(zSnapTo: number): void {
    if (this.crashCooldown > 0) return;
    this.speed = MAX_SPEED / 5;
    this.position = increase(zSnapTo, -PLAYER_Z, this.trackLength);
    this.crashCooldown = CRASH_COOLDOWN_TICKS;
    this.shakeTicksLeft = SHAKE_TICKS;
    this.crashFlashLeft = CRASH_FLASH_TICKS;
    this.phase = 'crashed';
    this.ctx.audio.sfx('crash');
    this.ctx.events.emit('death', { remainingLives: -1 });
  }

  private overlap(
    x1: number,
    w1: number,
    x2: number,
    w2: number,
    percent: number
  ): boolean {
    const half = percent / 2;
    const min1 = x1 - w1 * half;
    const max1 = x1 + w1 * half;
    const min2 = x2 - w2 * half;
    const max2 = x2 + w2 * half;
    return !(max1 < min2 || min1 > max2);
  }

  // --------------------------------------------------------------------------
  // Rendering helpers
  // --------------------------------------------------------------------------

  private renderBackground(g: CanvasRenderingContext2D, playerY: number): void {
    // Frame clear with horizon magenta — fills the entire canvas so any
    // gap between background and road takes the warm horizon tint instead
    // of leaving deep-black pixel space (which read as a hard black band
    // between mountains and road's vanishing point).
    g.fillStyle = SKY_HORIZON;
    g.fillRect(0, 0, WIDTH, HEIGHT);

    // Horizon line — drop below the canvas midpoint so the road's natural
    // vanishing point sits where the sky meets the mountains. Reference
    // racer puts horizon around 62% of canvas height; centering it (50%)
    // left a wide dead band of magenta sky between mountains and road's
    // farthest visible segment.
    const horizonY = HEIGHT * 0.62 + this.bgHill;

    // Sky gradient — top dark, horizon magenta. Extend slightly past
    // horizonY so the bleed below the mountains takes the warm tint.
    const grad = g.createLinearGradient(0, 0, 0, horizonY + 8);
    grad.addColorStop(0, SKY_TOP);
    grad.addColorStop(1, SKY_HORIZON);
    g.fillStyle = grad;
    g.fillRect(0, 0, WIDTH, horizonY + 8);

    // Sun behind the horizon.
    g.fillStyle = SUN;
    const sunX = WIDTH / 2 - 26 - (this.bgCurve % (WIDTH * 4)) * 0.04;
    const sunY = horizonY - 32;
    g.beginPath();
    g.arc(sunX, sunY, 22, 0, Math.PI * 2);
    g.fill();
    // Horizontal sun bands (vaporwave sun).
    g.fillStyle = SKY_HORIZON;
    for (let i = 0; i < 5; i++) {
      g.fillRect(sunX - 26, sunY - 10 + i * 7 + 4, 52, 2);
    }

    // Distant mountains as silhouettes.
    g.fillStyle = MOUNTAIN_FAR;
    g.beginPath();
    g.moveTo(0, horizonY);
    const farOffset = (this.bgCurve * 0.05) % 120;
    for (let x = -farOffset; x <= WIDTH + 80; x += 60) {
      g.lineTo(x + 30, horizonY - 18);
      g.lineTo(x + 60, horizonY);
    }
    g.lineTo(WIDTH, horizonY);
    g.closePath();
    g.fill();

    g.fillStyle = MOUNTAIN_NEAR;
    g.beginPath();
    g.moveTo(0, horizonY);
    const nearOffset = (this.bgCurve * 0.12) % 160;
    for (let x = -nearOffset; x <= WIDTH + 80; x += 80) {
      g.lineTo(x + 40, horizonY - 10);
      g.lineTo(x + 80, horizonY);
    }
    g.lineTo(WIDTH, horizonY);
    g.closePath();
    g.fill();

    // Suppress unused-arg warning; reserved for later horizon-line wobble.
    void playerY;
  }

  private renderSegment(g: CanvasRenderingContext2D, seg: Segment): void {
    const x1 = seg.p1.screen.x;
    const y1 = seg.p1.screen.y;
    const w1 = seg.p1.screen.w;
    const x2 = seg.p2.screen.x;
    const y2 = seg.p2.screen.y;
    const w2 = seg.p2.screen.w;

    const r1 = w1 / Math.max(6, 2 * 3);
    const r2 = w2 / Math.max(6, 2 * 3);
    const l1 = w1 / Math.max(32, 8 * 3);
    const l2 = w2 / Math.max(32, 8 * 3);

    // Grass — paint ONLY the area off the road (left flank + right flank),
    // NOT a full-canvas-width band. The previous full-width fillRect would
    // stack grass bands on top of each other when many far segments
    // projected to nearly the same screen-Y (typical when cresting hills
    // or going downhill), creating a magenta "horizon stripe" artifact.
    // The two trapezoids below let the sky show through above the road's
    // vanishing point, which is correct.
    const leftX1 = x1 - w1 - r1;
    const leftX2 = x2 - w2 - r2;
    const rightX1 = x1 + w1 + r1;
    const rightX2 = x2 + w2 + r2;
    this.poly(g, 0, y1, leftX1, y1, leftX2, y2, 0, y2, seg.color.grass);
    this.poly(g, rightX1, y1, WIDTH, y1, WIDTH, y2, rightX2, y2, seg.color.grass);

    // Rumble strips.
    this.poly(g, leftX1, y1, x1 - w1, y1, x2 - w2, y2, leftX2, y2, seg.color.rumble);
    this.poly(g, rightX1, y1, x1 + w1, y1, x2 + w2, y2, rightX2, y2, seg.color.rumble);
    // Road surface.
    this.poly(g, x1 - w1, y1, x1 + w1, y1, x2 + w2, y2, x2 - w2, y2, seg.color.road);

    // Lane stripes.
    if (seg.color.lane) {
      const lw1 = (w1 * 2) / 3;
      const lw2 = (w2 * 2) / 3;
      let lx1 = x1 - w1 + lw1;
      let lx2 = x2 - w2 + lw2;
      for (let lane = 1; lane < 3; lane++) {
        this.poly(
          g,
          lx1 - l1 / 2,
          y1,
          lx1 + l1 / 2,
          y1,
          lx2 + l2 / 2,
          y2,
          lx2 - l2 / 2,
          y2,
          seg.color.lane
        );
        lx1 += lw1;
        lx2 += lw2;
      }
    }

    // Fog: tint road + immediate flanks toward FOG_COLOR for far segments.
    // Match the road+rumble trapezoid shape so we don't paint into the sky;
    // that's what caused the "horizon stripe" stacking artifact.
    if (seg.fog < 1) {
      g.globalAlpha = 1 - seg.fog;
      this.poly(g, leftX1, y1, rightX1, y1, rightX2, y2, leftX2, y2, FOG_COLOR);
      g.globalAlpha = 1;
    }
  }

  private poly(
    g: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    x3: number,
    y3: number,
    x4: number,
    y4: number,
    color: string
  ): void {
    g.fillStyle = color;
    g.beginPath();
    g.moveTo(x1, y1);
    g.lineTo(x2, y2);
    g.lineTo(x3, y3);
    g.lineTo(x4, y4);
    g.closePath();
    g.fill();
  }

  private renderRoadsideSprite(
    g: CanvasRenderingContext2D,
    sp: RoadsideSprite,
    scale: number,
    x: number,
    y: number,
    clipY: number
  ): void {
    // Sprite sizing: use `worldW * scale * (W/2)` exactly like the reference's
    // billboard math — this is consistent with how segment.screen.w is
    // computed (worldW=2000 → screen.w in pixels). worldW values are
    // calibrated per-kind in road.ts to give reasonable on-screen sizes
    // at typical visible distance.
    //
    // Cull below 6px wide: at sub-pixel resolution our shape-based drawing
    // produces horizontal stripes (small h * 0.7 = 0px panel + 1px border),
    // which read as artifacts at the horizon. 6px is the pixel-art floor
    // where a recognizable sprite silhouette can still form.
    const destW = sp.worldW * scale * (WIDTH / 2);
    if (destW < 6) return;
    // Source aspect ratios per kind (pixel-art atlas reference).
    const aspect =
      sp.kind === 'palm' ? 56 / 40 : sp.kind === 'sign' ? 36 / 28 : sp.kind === 'rock' ? 22 / 30 : 56 / 24;
    const destH = destW * aspect;
    const destX = x - destW / 2 + (sp.offset < 0 ? -destW : 0);
    const destY = y - destH;

    // Depth occlusion: the segment-pass tracks `maxY` — the highest-on-screen
    // Y that a closer segment has already painted to. A sprite stands on the
    // ground at `destY + destH`; if that bottom-Y is BELOW clipY (greater Y,
    // i.e., further down the canvas), the sprite's lower body would paint
    // over a closer road segment. Clip away anything below clipY. Cull
    // entirely if the sprite is fully below clipY (nothing visible).
    if (destY >= clipY) return;
    const needsClip = destY + destH > clipY;
    if (needsClip) {
      g.save();
      g.beginPath();
      g.rect(0, 0, WIDTH, clipY);
      g.clip();
    }

    switch (sp.kind) {
      case 'palm':
        this.drawPalm(g, destX, destY, destW, destH);
        break;
      case 'sign':
        this.drawSign(g, destX, destY, destW, destH);
        break;
      case 'rock':
        this.drawRock(g, destX, destY, destW, destH);
        break;
      case 'neonPink':
        this.drawNeon(g, destX, destY, destW, destH, SPRITE_NEON_PINK);
        break;
      case 'neonCyan':
        this.drawNeon(g, destX, destY, destW, destH, SPRITE_NEON_CYAN);
        break;
    }

    if (needsClip) {
      g.restore();
    }
  }

  private drawPalm(
    g: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number
  ): void {
    const trunkW = Math.max(2, w * 0.1);
    g.fillStyle = SPRITE_PALM_TRUNK;
    g.fillRect(x + w / 2 - trunkW / 2, y + h * 0.5, trunkW, h * 0.5);
    g.fillStyle = SPRITE_PALM_FRONDS;
    g.fillRect(x + w * 0.15, y + h * 0.35, w * 0.7, h * 0.18);
    g.fillRect(x + w * 0.05, y + h * 0.25, w * 0.9, h * 0.13);
    g.fillRect(x + w * 0.35, y + h * 0.1, w * 0.3, h * 0.25);
    g.fillStyle = SPRITE_PALM_HI;
    g.fillRect(x + w * 0.4, y + h * 0.2, w * 0.2, h * 0.06);
  }

  private drawSign(
    g: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number
  ): void {
    const postW = Math.max(2, w * 0.08);
    g.fillStyle = SPRITE_SIGN_POST;
    g.fillRect(x + w / 2 - postW / 2, y + h * 0.55, postW, h * 0.45);
    g.fillStyle = SPRITE_SIGN_YELLOW;
    g.fillRect(x + w * 0.1, y + h * 0.1, w * 0.8, h * 0.45);
    g.fillStyle = SPRITE_SIGN_BORDER;
    g.fillRect(x + w * 0.1, y + h * 0.1, w * 0.8, h * 0.05);
    g.fillRect(x + w * 0.1, y + h * 0.5, w * 0.8, h * 0.05);
    g.fillRect(x + w * 0.1, y + h * 0.1, w * 0.05, h * 0.45);
    g.fillRect(x + w * 0.85, y + h * 0.1, w * 0.05, h * 0.45);
    g.fillRect(x + w * 0.3, y + h * 0.25, w * 0.4, h * 0.15);
  }

  private drawRock(
    g: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number
  ): void {
    g.fillStyle = '#777';
    g.fillRect(x + w * 0.1, y + h * 0.2, w * 0.8, h * 0.7);
    g.fillStyle = '#aaa';
    g.fillRect(x + w * 0.2, y + h * 0.1, w * 0.4, h * 0.25);
    g.fillStyle = '#444';
    g.fillRect(x + w * 0.1, y + h * 0.8, w * 0.8, h * 0.1);
  }

  private drawNeon(
    g: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    color: string
  ): void {
    // Tall thin neon sign post.
    const postW = Math.max(2, w * 0.06);
    g.fillStyle = SPRITE_SIGN_POST;
    g.fillRect(x + w / 2 - postW / 2, y + h * 0.7, postW, h * 0.3);
    g.fillStyle = color;
    g.fillRect(x + w * 0.3, y, w * 0.4, h * 0.7);
    g.fillStyle = 'rgba(255,255,255,0.5)';
    g.fillRect(x + w * 0.4, y + h * 0.05, w * 0.2, h * 0.6);
  }

  private renderCarSprite(
    g: CanvasRenderingContext2D,
    car: OpponentCar,
    scale: number,
    x: number,
    y: number,
    clipY: number
  ): void {
    // Mirror reference billboard math: worldW * scale * (W/2).
    // Cull below 8px for same sub-pixel pixel-art floor reason as roadside
    // sprites (see renderRoadsideSprite).
    const destW = car.worldW * scale * (WIDTH / 2);
    if (destW < 8) return;
    const aspect = car.kind === 'truck' ? 22 / 32 : 18 / 28;
    const destH = destW * aspect;
    const destX = x - destW / 2;
    const destY = y - destH;

    // Depth occlusion (see renderRoadsideSprite for explanation): cars stand
    // on the road; their bottom-Y sits at the segment surface. Clip away
    // anything below clipY since that would overlap a closer segment.
    if (destY >= clipY) return;
    const needsClip = destY + destH > clipY;
    if (needsClip) {
      g.save();
      g.beginPath();
      g.rect(0, 0, WIDTH, clipY);
      g.clip();
    }

    const body =
      car.kind === 'carRed'
        ? TRAFFIC_MAGENTA
        : car.kind === 'carBlue'
          ? TRAFFIC_PURPLE
          : car.kind === 'carYellow'
            ? PLAYER_BODY_LIGHT
            : car.kind === 'carGreen'
              ? TRAFFIC_GREEN
              : TRAFFIC_CYAN;

    // Shadow.
    g.fillStyle = TRAFFIC_SHADOW;
    g.fillRect(destX + destW * 0.05, destY + destH * 0.85, destW * 0.9, destH * 0.15);
    // Body.
    g.fillStyle = body;
    g.fillRect(destX + destW * 0.1, destY + destH * 0.35, destW * 0.8, destH * 0.5);
    g.fillRect(destX + destW * 0.2, destY + destH * 0.15, destW * 0.6, destH * 0.25);
    // Window.
    g.fillStyle = '#1a3a55';
    g.fillRect(destX + destW * 0.28, destY + destH * 0.2, destW * 0.44, destH * 0.2);
    g.fillStyle = '#7ab7e0';
    g.fillRect(destX + destW * 0.3, destY + destH * 0.22, destW * 0.4, destH * 0.06);
    // Taillights.
    g.fillStyle = PLAYER_TAIL;
    g.fillRect(destX + destW * 0.12, destY + destH * 0.55, destW * 0.1, destH * 0.15);
    g.fillRect(destX + destW * 0.78, destY + destH * 0.55, destW * 0.1, destH * 0.15);
    // Wheels.
    g.fillStyle = '#1a1a1a';
    g.fillRect(destX + destW * 0.02, destY + destH * 0.65, destW * 0.12, destH * 0.25);
    g.fillRect(destX + destW * 0.86, destY + destH * 0.65, destW * 0.12, destH * 0.25);

    if (needsClip) {
      g.restore();
    }
  }

  private renderPlayer(
    g: CanvasRenderingContext2D,
    visualSteer: number,
    braking: boolean
  ): void {
    const cx = WIDTH / 2;
    const cy = PLAYER_SCREEN_Y;
    const w = PLAYER_SPRITE_W;
    const h = PLAYER_SPRITE_H;
    const lean = visualSteer * 3;

    const x = cx - w / 2 + lean;
    const y = cy - h / 2;

    // Shadow.
    g.fillStyle = 'rgba(0,0,0,0.45)';
    g.fillRect(x - 2, y + h - 1, w + 4, 3);

    // Body.
    g.fillStyle = PLAYER_BODY;
    g.fillRect(x, y + h * 0.35, w, h * 0.55);
    g.fillRect(x + w * 0.15, y + h * 0.1, w * 0.7, h * 0.3);
    // Roof shadow.
    g.fillStyle = PLAYER_BODY_DARK;
    g.fillRect(x + w * 0.2, y + h * 0.05, w * 0.6, h * 0.15);
    // Windshield.
    g.fillStyle = PLAYER_WINDOW;
    g.fillRect(x + w * 0.22, y + h * 0.2, w * 0.56, h * 0.18);
    g.fillStyle = PLAYER_WINDOW_HI;
    g.fillRect(x + w * 0.24, y + h * 0.2, w * 0.52, h * 0.06);
    // Hood highlight.
    g.fillStyle = PLAYER_BODY_LIGHT;
    g.fillRect(x + w * 0.18, y + h * 0.55, w * 0.64, 2);
    // Taillights — brighter when braking.
    g.fillStyle = braking ? '#ff8080' : PLAYER_TAIL;
    g.fillRect(x + w * 0.1, y + h * 0.7, w * 0.12, h * 0.18);
    g.fillRect(x + w * 0.78, y + h * 0.7, w * 0.12, h * 0.18);
    // Wheels.
    g.fillStyle = '#0a0a0a';
    g.fillRect(x - 2, y + h * 0.7, 5, h * 0.3);
    g.fillRect(x + w - 3, y + h * 0.7, 5, h * 0.3);
  }

  private renderHUD(g: CanvasRenderingContext2D): void {
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';
    g.textAlign = 'left';

    g.fillStyle = HUD_TEXT;
    g.fillText('SCORE', 8, 6);
    g.fillStyle = HUD_SCORE;
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), 8, 18);

    g.textAlign = 'right';
    g.fillStyle = HUD_TEXT;
    g.fillText('SPEED', WIDTH - 8, 6);
    g.fillStyle = HUD_SPEED;
    g.font = 'bold 14px ui-monospace, monospace';
    g.fillText(String(Math.floor((this.speed / MAX_SPEED) * 220)), WIDTH - 8, 18);

    g.font = '10px ui-monospace, monospace';
    g.textAlign = 'center';
    g.fillStyle = HUD_TEXT;
    g.fillText(`LAP ${clamp(this.lap, 1, TOTAL_LAPS)}/${TOTAL_LAPS}`, WIDTH / 2, 6);
    g.fillText(`TIME ${this.elapsed.toFixed(1)}`, WIDTH / 2, 18);

    this.renderTrackProgress(g);

    // TURBO indicator sits under SPEED in the top-right column. Previously
    // bottom-center (HEIGHT - 24) which painted directly over the player
    // car at HEIGHT - 32. Co-locating with SPEED reads as "speed boost
    // active" without obscuring gameplay.
    if (this.inTurbo && this.speed > MAX_SPEED * 0.4) {
      g.font = 'bold 10px ui-monospace, monospace';
      g.textAlign = 'right';
      g.fillStyle = HUD_TURBO;
      g.fillText('TURBO', WIDTH - 8, 34);
    }
  }

  /**
   * Linear progress bar across the bottom showing the player's position on
   * the current lap. Start/finish line is the marker at both ends (the
   * track is circular — start IS finish). Player position is a yellow
   * triangle tick.
   */
  private renderTrackProgress(g: CanvasRenderingContext2D): void {
    const barX = 40;
    const barY = HEIGHT - 10;
    const barW = WIDTH - 80;
    const barH = 3;

    // Bar background (dark groove).
    g.fillStyle = 'rgba(0,0,0,0.6)';
    g.fillRect(barX - 1, barY - 1, barW + 2, barH + 2);

    // Progress fill — how far through the current lap.
    const progress = clamp(this.position / this.trackLength, 0, 1);
    g.fillStyle = HUD_TEXT;
    g.fillRect(barX, barY, barW * progress, barH);

    // Start/finish markers (left edge, right edge).
    g.fillStyle = '#ffffff';
    g.fillRect(barX - 2, barY - 2, 2, barH + 4);
    g.fillRect(barX + barW, barY - 2, 2, barH + 4);

    // Checkered marker on the finish (right edge): two stacked pixels.
    g.fillStyle = '#000000';
    g.fillRect(barX + barW, barY - 1, 1, 1);
    g.fillRect(barX + barW + 1, barY + 1, 1, 1);

    // Player position tick — yellow triangle pointing down at the bar.
    const playerTickX = Math.round(barX + barW * progress);
    g.fillStyle = HUD_SCORE;
    g.fillRect(playerTickX - 2, barY - 4, 4, 1);
    g.fillRect(playerTickX - 1, barY - 3, 2, 1);
    g.fillRect(playerTickX, barY - 2, 1, 1);

    // Labels.
    g.font = '8px ui-monospace, monospace';
    g.textBaseline = 'bottom';
    g.textAlign = 'left';
    g.fillStyle = HUD_TEXT;
    g.fillText('START', barX - 2, barY - 6);
    g.textAlign = 'right';
    g.fillText('FINISH', barX + barW + 2, barY - 6);
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0,0,0,0.72)';
    g.fillRect(0, 0, WIDTH, HEIGHT);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = '24px ui-monospace, monospace';
    g.fillStyle = this.phase === 'finished' ? PLAYER_BODY : CRASH_FLASH;
    g.fillText(this.phase === 'finished' ? 'FINISH!' : 'GAME OVER', WIDTH / 2, HEIGHT / 2 - 24);

    g.font = '14px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(
      `SCORE ${String(this.finalScore).padStart(6, '0')}`,
      WIDTH / 2,
      HEIGHT / 2
    );
    if (this.phase === 'finished') {
      g.fillStyle = HUD_TEXT;
      g.fillText(`TIME ${this.finalElapsed.toFixed(2)}`, WIDTH / 2, HEIGHT / 2 + 18);
    }

    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_TEXT;
    g.fillText('PRESS Z TO RETRY', WIDTH / 2, HEIGHT / 2 + 42);
  }
}
